import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';

const NAV = [
  {
    section: null,
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: 'M3 3h4v4H3zM9 3h4v4H9zM3 9h4v4H3zM9 9h4v4H9z' },
    ]
  },
  {
    section: 'Empleados',
    plans: ['control', 'gestion', 'empresa'],
    items: [
      { path: '/empleados', label: 'Empleados', icon: 'M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-5 8s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3z' },
      { path: '/fichajes', label: 'Fichajes', icon: 'M2 4h12v10H2zM5 4V2.5M11 4V2.5M2 8h12' },
      { path: '/ausencias', label: 'Ausencias', icon: 'M2 3h12v2H2zM4 7h8v7H4z' },
    ]
  },
  {
    section: 'Proyectos',
    plans: ['gestion', 'empresa'],
    items: [
      { path: '/proyectos', label: 'Proyectos', icon: 'M2 2h12v12H2zM5 7h6M5 5h6M5 9h4' },
      { path: '/clientes', label: 'Clientes', icon: 'M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 14s0-4 6-4 6 4 6 4' },
      { path: '/horas', label: 'Horas', icon: 'M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2zM8 5v3l2.5 2.5' },
      { path: '/imputacion', label: 'Imputacion', icon: 'M3 3h10v2H3zM3 7h7v2H3zM3 11h10v2H3zM12 7l2 2-2 2' },
    ]
  },
  {
    section: 'Administración',
    plans: ['control', 'gestion', 'empresa'],
    adminOnly: true,
    items: [
      { path: '/reportes', label: 'Reportes', icon: 'M3 4h10M4 8h8M5 12h5' },
      { path: '/configuracion', label: 'Configuración', icon: 'M8 5a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM2 8h1M15 8h1M8 2v1M8 15v1' },
    ]
  },
];

export default function Layout({ children }) {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [plan, setPlan] = useState(usuario?.plan || 'control');
  const isAdmin = usuario?.rol === 'admin';

  useEffect(() => {
    API.get('/auth/me').then(res => {
      if (res.data?.plan) setPlan(res.data.plan);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = usuario?.nombre?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'U';

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const sidebarStyle = {
    ...styles.sidebar,
    ...(isMobile ? {
      transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
      transition: 'transform 0.25s ease',
      zIndex: 1000,
      boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.13)' : 'none',
    } : {}),
  };

  return (
    <div style={styles.app}>
      {isMobile && sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={styles.overlay} />}

      {isMobile && (
        <div style={styles.topbar}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={styles.hamburger}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              {sidebarOpen
                ? <path d="M4 4l12 12M16 4L4 16" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round"/>
                : <path d="M3 5h14M3 10h14M3 15h14" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round"/>
              }
            </svg>
          </button>
          <div style={styles.topbarLogo}>Tempo<span style={styles.logoDot}>.</span>Gestión</div>
          <div style={styles.avatar}>{initials}</div>
        </div>
      )}

      <div style={sidebarStyle}>
        <div style={styles.logoBox}>
          <div style={styles.logo}>Tempo<span style={styles.logoDot}>.</span>Gestión</div>
          <div style={styles.empresa}>{usuario?.nombre || ''}</div>
        </div>

        <nav style={styles.nav}>
          {NAV.map((group, gi) => {
            // Si el grupo tiene restricción de plan, verificar
            if (group.plans && !group.plans.includes(plan)) {
              // Mostrar bloqueado si no tiene el plan
              return (
                <div key={gi}>
                  {group.section && (
                    <div style={styles.sectionLabel}>
                      {group.section}
                      <span style={styles.lockBadge}>🔒</span>
                    </div>
                  )}
                  {group.items.map(item => (
                    <div key={item.path} style={styles.navItemLocked} title="Actualiza tu plan para acceder">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d={item.icon} stroke="#ddd" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span style={{color:'#ccc'}}>{item.label}</span>
                    </div>
                  ))}
                </div>
              );
            }

            // Si es solo admin y el usuario no es admin, no mostrar
            if (group.adminOnly && !isAdmin) return null;

            return (
              <div key={gi}>
                {group.section && <div style={styles.sectionLabel}>{group.section}</div>}
                {group.items.map(item => (
                  <Link key={item.path} to={item.path} style={{...styles.navItem, ...(isActive(item.path) ? styles.navActive : {})}}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d={item.icon} stroke={isActive(item.path) ? '#378ADD' : 'currentColor'} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {item.label}
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>

        <div style={styles.userBox}>
          <div style={styles.avatar}>{initials}</div>
          <div style={styles.userInfo}>
            <div style={styles.userName}>{usuario?.nombre}</div>
            <div style={styles.userRole}>{isAdmin ? 'Administrador' : 'Empleado'}</div>
          </div>
          <button onClick={handleLogout} style={styles.logoutBtn} title="Cerrar sesión">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 2H2v10h3M10 4l3 3-3 3M13 7H6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <div style={{...styles.main, ...(isMobile ? {marginLeft:0} : {})}}>
        {isMobile && <div style={{height:52}} />}
        {children}
      </div>
    </div>
  );
}

const styles = {
  app: { display: 'flex', minHeight: '100vh', background: '#f5f5f3', fontFamily: '-apple-system, BlinkMacSystemFont, Inter, sans-serif' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 999 },
  topbar: { position: 'fixed', top: 0, left: 0, right: 0, height: 52, background: 'white', borderBottom: '0.5px solid #e0e0d8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', zIndex: 998 },
  topbarLogo: { fontSize: 15, fontWeight: 500, color: '#1a1a1a' },
  hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' },
  sidebar: { width: 220, minWidth: 220, background: 'white', borderRight: '0.5px solid #e0e0d8', display: 'flex', flexDirection: 'column', position: 'fixed', height: '100vh', top: 0, left: 0 },
  logoBox: { padding: '20px 16px 16px', borderBottom: '0.5px solid #e0e0d8' },
  logo: { fontSize: 15, fontWeight: 500, color: '#1a1a1a' },
  logoDot: { color: '#378ADD' },
  empresa: { fontSize: 11, color: '#aaa', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  nav: { padding: 8, flex: 1, overflowY: 'auto' },
  sectionLabel: { fontSize: 11, color: '#bbb', padding: '10px 8px 4px', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 4 },
  lockBadge: { fontSize: 9, marginLeft: 2 },
  navItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 8, fontSize: 13, color: '#666', textDecoration: 'none', marginBottom: 2 },
  navItemLocked: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 8, fontSize: 13, color: '#ccc', marginBottom: 2, cursor: 'not-allowed' },
  navActive: { background: '#E6F1FB', color: '#185FA5', fontWeight: 500 },
  userBox: { padding: '12px 8px', borderTop: '0.5px solid #e0e0d8', display: 'flex', alignItems: 'center', gap: 8 },
  avatar: { width: 28, height: 28, borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, color: '#185FA5', flexShrink: 0 },
  userInfo: { flex: 1, minWidth: 0 },
  userName: { fontSize: 13, fontWeight: 500, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userRole: { fontSize: 11, color: '#aaa' },
  logoutBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 4, display: 'flex', alignItems: 'center' },
  main: { flex: 1, marginLeft: 220, minHeight: '100vh' },
};
